<template>
  <div class="app-container">
    <!-- §10.4 最小统计：今日 / 近7天 -->
    <el-card class="box-card" style="margin-bottom: 10px">
      <ai-cs-stats />
    </el-card>
    <!-- 筛选 -->
    <el-card class="box-card">
      <div class="filter-container">
        <el-form :model="query" ref="queryForm" :inline="true">
          <el-form-item label="状态：">
            <el-select v-model="query.status" placeholder="全部" size="small" clearable style="width: 140px">
              <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="处理方式：">
            <el-select v-model="query.handler" placeholder="全部" size="small" clearable style="width: 150px">
              <el-option v-for="item in handlerOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="客户：">
            <el-input v-model="query.customerName" placeholder="客户姓名" size="small" clearable style="width: 160px" />
          </el-form-item>
          <el-form-item label="提交时间：">
            <el-date-picker
              v-model="dateRange"
              type="datetimerange"
              size="small"
              value-format="yyyy-MM-dd HH:mm:ss"
              range-separator="至"
              start-placeholder="开始"
              end-placeholder="结束" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" size="mini" @click="handleQuery">搜索</el-button>
            <el-button type="info" size="mini" plain @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>

    <el-card class="box-card mt10">
      <!-- 积压提示。原方案是"定时任务发站内消息"，2026-09-15 用户改为页面提示：
           不新增通道、不污染 msg_type 口径，客服打开页面就能看见。 -->
      <el-alert
        v-if="overdueNum > 0"
        class="mb10"
        type="warning"
        show-icon
        :closable="false"
        :title="`有 ${overdueNum} 条待审核已超过 ${overdueHours} 小时未处理`" />
      <div class="pending-tip mb10">
        待审核 <span class="pending-num">{{ pendingNum }}</span> 条
      </div>

      <el-table v-loading="listLoading" :data="list" :row-class-name="rowClassName">
        <el-table-column label="客户" align="center" width="150">
          <template slot-scope="{row}">
            <div>{{ row.customerName || '-' }}</div>
          </template>
        </el-table-column>
        <!-- 图片问题（questionType=2）现在也入队：问题文字可能为空，只有一张图。不单独处理的话这一格是空白，看不出客户问了什么 -->
        <el-table-column label="客户问题" align="left" min-width="220" show-overflow-tooltip>
          <template slot-scope="{row}">
            <el-image
              v-if="Number(row.questionType) === 2 && row.imageUrl"
              :src="row.imageUrl"
              :preview-src-list="[row.imageUrl]"
              fit="cover"
              style="width:40px;height:40px;vertical-align:middle;margin-right:6px;border-radius:3px"
            />
            <span>{{ row.questionText || (Number(row.questionType) === 2 ? '（图片）' : '-') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="来源" align="center" width="110">
          <template slot-scope="{row}">
            <el-tag v-if="row.answerSource === 'RAG'" size="mini" type="success">知识库</el-tag>
            <el-tag v-else-if="row.answerSource === 'QWEN'" size="mini" type="warning">AI兜底</el-tag>
            <el-tag v-else size="mini" type="info">无</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="草稿" prop="draftAnswer" align="left" min-width="260" show-overflow-tooltip>
          <template slot-scope="{row}">
            <span v-if="row.draftAnswer">{{ row.draftAnswer }}</span>
            <span v-else class="empty-text">无草稿，需人工作答</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" align="center" width="110">
          <template slot-scope="{row}">
            <el-tag :type="statusTagType(row.status)" size="mini">{{ statusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" prop="createdTime" align="center" width="160" />
        <!--
          REQ-2026-574277 二期修订 §4：本页改成**只读**的「AI回复记录」。
          发送 / 编辑后发送 / 忽略 三个操作统一在「消息管理」工作台的内联草稿卡片里做，
          **这里不再留入口** —— 同一件事两个入口，迟早有一个没人维护。
          要处理待审草稿请到 客服服务 → 消息管理。
        -->
        <el-table-column label="实际发出" prop="finalAnswer" align="left" min-width="220" show-overflow-tooltip>
          <template slot-scope="{row}">
            <span v-if="row.finalAnswer">{{ row.finalAnswer }}</span>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>
        <el-table-column label="归属客服" align="center" width="110">
          <template slot-scope="{row}">
            <span>{{ row.assigneeName || '-' }}</span>
            <span v-if="row.transferCount > 0" class="danger-text">（转{{ row.transferCount }}次）</span>
          </template>
        </el-table-column>
        <el-table-column label="处理人" align="center" width="120">
          <template slot-scope="{row}">
            <!-- 系统取值（合并/归档/批量）标灰，一眼和真人处理区分开 -->
            <el-tag v-if="isSystemHandler(row.reviewerName)" size="mini" type="info">{{ row.reviewerName }}</el-tag>
            <span v-else>{{ row.reviewerName || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="处理时间" prop="reviewedAt" align="center" width="160" />
        <el-table-column label="操作" align="center" width="80" fixed="right">
          <template slot-scope="{row}">
            <el-button type="text" @click="handleDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <pagination v-show="total > 0" :total="total" :pageNo.sync="query.pageNo" :pageSize.sync="query.pageSize" @pagination="doQueryList" />
    </el-card>

    <!-- 详情：会话上下文（只读） -->
    <el-dialog title="会话详情" :visible.sync="detailVisible" width="720px" :close-on-click-modal="false">
      <div class="context-box" v-loading="contextLoading">
        <div v-if="!contextList.length" class="empty-text">暂无会话记录</div>
        <div v-for="(item, index) in contextList" :key="index" class="context-line" :class="{ 'is-platform': isPlatform(item) }">
          <div class="context-meta">
            <span class="context-who">{{ isPlatform(item) ? '客服' : '客户' }}</span>
            <el-tag v-if="aiFlagText(item.aiFlag)" size="mini" type="info" class="ml5">{{ aiFlagText(item.aiFlag) }}</el-tag>
            <span class="context-time">{{ item.createdTime }}</span>
          </div>
          <div class="context-content">{{ item.messageContent }}</div>
        </div>
      </div>

    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import AiCsStats from '@/components/AiCsStats'
import aiReviewApi from '@/api/im/aiReview'

// 与后端 CustServiceAiReviewPO 的常量一一对应，改一边要改两边
const STATUS = {
  PENDING: 1,
  SENT: 2,
  IGNORED: 3,
  AUTO_SENT: 4
}

export default {
  name: 'AiCsReview',
  components: { Pagination, AiCsStats },
  data() {
    return {
      listLoading: false,
      list: [],
      total: 0,
      pendingNum: 0,
      overdueNum: 0,
      // 阈值由后端给，不在前端写死——后端改了阈值前端跟着变
      overdueHours: 2,
      dateRange: [],
      // 默认看**全部**：这一页现在是回查记录用的，不再是待办箱
      // （待办箱在 客服服务 → 消息管理 的内联草稿卡片里）
      query: { status: null, handler: null, customerName: null, pageNo: 1, pageSize: 10 },
      // 处理方式。⚠️ 取值与后端 AiCsReviewService_TEE 的 HANDLER_* 常量一一对应，改一边要改两边。
      // 「超时未回复」= 系统(归档)，是最该盯的一类，**别和人工忽略混在一起看**。
      handlerOptions: [
        { value: 'MANUAL', label: '人工处理' },
        { value: 'SYS_ARCHIVE', label: '超时未回复(系统归档)' },
        { value: 'SYS_MERGE', label: '被新消息合并(系统)' },
        { value: 'BATCH', label: '批量处理' }
      ],
      statusOptions: [
        { value: STATUS.PENDING, label: '待审核' },
        { value: STATUS.SENT, label: '已发送' },
        { value: STATUS.IGNORED, label: '已忽略' },
        { value: STATUS.AUTO_SENT, label: '自动已发' }
      ],
      detailVisible: false,
      contextLoading: false,
      contextList: [],
      current: {}
    }
  },
  created() {
    this.doQueryList({ pageNo: 1 })
    this.loadPendingCount()
  },
  methods: {
    statusText(status) {
      const map = { 1: '待审核', 2: '已发送', 3: '已忽略', 4: '自动已发' }
      return map[status] || '-'
    },
    statusTagType(status) {
      const map = { 1: 'warning', 2: 'success', 3: 'info', 4: 'success' }
      return map[status] || 'info'
    },
    /**
     * im_conversation_message.ai_flag：0人工 1RAG直发 2审核后发 3首响/等待提示 4人工后台回复
     * 0 不标——绝大多数历史消息都是 0，标了反而看不清重点。
     */
    aiFlagText(aiFlag) {
      const map = { 1: '知识库自动回复', 2: '审核后发送', 3: '系统提示', 4: '人工回复' }
      return map[aiFlag] || ''
    },
    /**
     * reviewer_name 里的系统取值：系统(合并) / 系统(归档) / xxx(批量)。
     * 约定是"括号里带 合并/归档/批量"，见后端 HANDLER_* 常量。
     */
    isSystemHandler(name) {
      return !!name && /[（(](合并|归档|批量)[）)]$/.test(name)
    },
    /** from_user_type=40 是客服/平台侧 */
    isPlatform(item) {
      return item && item.fromUserType === 40
    },
    handleQuery() {
      this.doQueryList({ pageNo: 1 })
    },
    handleReset() {
      this.dateRange = []
      this.query = { status: null, handler: null, customerName: null, pageNo: 1, pageSize: 10 }
      this.doQueryList({ pageNo: 1 })
    },
    doQueryList({ pageNo, pageSize } = {}) {
      if (pageNo) this.query.pageNo = pageNo
      if (pageSize) this.query.pageSize = pageSize
      const params = { ...this.query }
      if (this.dateRange && this.dateRange.length === 2) {
        params.createdFrom = this.dateRange[0]
        params.createdTo = this.dateRange[1]
      }
      this.listLoading = true
      return aiReviewApi.fetchList(params)
        .then(res => {
          const data = res.data || {}
          this.list = data.list || []
          this.total = data.total || 0
        })
        .finally(() => { this.listLoading = false })
    },
    loadPendingCount() {
      aiReviewApi.pendingCount().then(res => {
        const data = res.data || {}
        this.pendingNum = data.pending || 0
        this.overdueNum = data.overdue || 0
        if (data.overdueHours) this.overdueHours = data.overdueHours
      })
    },
    /**
     * 超时未处理的行标红。
     *
     * ⚠️ 只对**待审核**的行判超时：已发送/已忽略的行 createdTime 同样很久以前，
     * 不加状态判断会把处理完的历史记录全标成红的，红色就失去意义了。
     */
    rowClassName({ row }) {
      if (row.status !== STATUS.PENDING || !row.createdTime) {
        return ''
      }
      const created = new Date(String(row.createdTime).replace(/-/g, '/')).getTime()
      if (!created) {
        return ''
      }
      return Date.now() - created > this.overdueHours * 3600 * 1000 ? 'row-overdue' : ''
    },
    handleDetail(row) {
      this.current = row
      this.contextList = []
      this.detailVisible = true
      this.contextLoading = true
      aiReviewApi.context(row.id)
        .then(res => { this.contextList = res.data || [] })
        .finally(() => { this.contextLoading = false })
    }
  }
}
</script>

<style scoped>
.pending-tip {
  font-size: 13px;
  color: #606266;
}
.pending-num {
  color: #e6a23c;
  font-weight: 700;
  font-size: 16px;
}
.empty-text {
  color: #909399;
  font-size: 12px;
}
.context-box {
  max-height: 340px;
  overflow-y: auto;
  padding: 8px 4px;
  background: #fafafa;
  border-radius: 4px;
}
.context-line {
  margin-bottom: 12px;
}
.context-meta {
  font-size: 12px;
  color: #909399;
}
.context-who {
  font-weight: 700;
  color: #606266;
}
.context-line.is-platform .context-who {
  color: #4680e3;
}
.context-time {
  margin-left: 8px;
}
.context-content {
  margin-top: 2px;
  font-size: 13px;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-all;
}
.reply-box {
  margin-top: 14px;
}
.reply-actions {
  margin-top: 8px;
  text-align: right;
}
.question-recap {
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
}
.recap-label {
  font-size: 12px;
  color: #909399;
}
.recap-text {
  font-size: 13px;
  color: #303133;
  margin-top: 2px;
}
.ml5 {
  margin-left: 5px;
}
.danger-text {
  color: #f56c6c;
}
/* 超时未处理：整行淡红底。用 ::v-deep 穿透 scoped，否则 el-table 生成的行选不中 */
::v-deep .el-table .row-overdue {
  background-color: #fef0f0;
}
::v-deep .el-table .row-overdue:hover > td {
  background-color: #fde2e2 !important;
}
</style>
